import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  Drawer,
  Typography,
  CardActionArea,
} from '@mui/material';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import Logo from 'src/minimals/Logo';
import Scrollbar from 'src/minimals/Scrollbar';
import NavSection from 'src/minimals/NavSection';
import { MHidden } from 'src/minimals/@material-extend';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';
import adminSidebarConfig from './AdminSidebarConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool,
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <CardActionArea
      onClick={onToggleCollapse}
      sx={{
        width: 24,
        height: 24,
        display: 'flex',
        cursor: 'pointer',
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'solid 1px',
        ...(collapseClick && {
          borderWidth: 2,
        }),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'currentColor',
        }}
      />
    </CardActionArea>
  );
}

AdminSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function AdminSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  const sidebarConfig = adminSidebarConfig();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center',
          }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isCollapse ? (
              <Logo />
            ) : (
              <Logo logoSrc={'/logo_main_white_nobg.png'} width={140} />
            )}
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse
                onToggleCollapse={onToggleCollapse}
                collapseClick={collapseClick}
              />
            )}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <Box
            sx={{
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          >
            <AdminPanelSettingsIcon sx={{ color: '#fff' }} />
          </Box>
        ) : (
          <AccountStyle>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'primary.main',
              }}
            >
              <AdminPanelSettingsIcon sx={{ color: '#fff' }} />
            </Box>
            <Box
              sx={{
                ml: 2,
                color: 'primary.contrastText',
                overflowWrap: 'break-word',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  maxWidth: `calc(${DRAWER_WIDTH}px - 130px)`,
                }}
              >
                {t('admin.sidebar.title')}
              </Typography>
              <Typography variant="body2">
                {t('admin.sidebar.subtitle')}
              </Typography>
            </Box>
          </AccountStyle>
        )}
      </Stack>

      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              backgroundColor: 'primary.darker',
            },
          }}
          transitionDuration={0}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              backgroundColor: 'primary.darker',
              color: '#fff',

              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.88),
              }),
            },
          }}
          transitionDuration={0}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
